/* =============================================================
   ConsultationServicePage — Individual consultation service page
   URL: /consultation/:country/:serviceId
   ============================================================= */
import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { ArrowLeft, CheckCircle, Clock, ExternalLink, Star, ChevronDown, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TRAVEL_LINK = "https://paylink.mn/api/s/inv_mrzx7kegbce69b";
const STUDY_LINK = "https://paylink.mn/api/s/inv_mrzx8tr30dd7d5";
const IMMIGRATION_LINK = "https://paylink.mn/api/s/inv_mrzxadzm4dbfd5";
const VIP_LINK = "https://paylink.mn/api/s/inv_mrzxbxln5bf605";

interface Service {
  id: string;
  name: string;
  description: string;
  bullets: string[];
  duration: string;
  price: number;
  qpayLink: string;
  badge?: string;
  badgeColor?: string;
}

const VIP_SERVICE = (countryName: string): Service => ({
  id: `${countryName.toLowerCase()}-vip`,
  name: `${countryName} VIP уулзалт`,
  description: "Дээрхи бүх зөвлөгөөнүүд багтсан иж бүрэн уулзалт",
  bullets: [
    "Таны кэйст дэлгэрэнгүй шинжилгээ хийх",
    "Хууль ёсоор ажиллаж амьдрах боломжийн тойм",
    "Дараагийн алхамуудын нарийвчилсан зааварчилгаа",
  ],
  duration: "2 цаг",
  price: 199000,
  qpayLink: VIP_LINK,
  badge: "Хамгийн иж бүрэн",
  badgeColor: "from-yellow-500 to-orange-500",
});

const ALL_SERVICES: Record<string, Service[]> = {
  usa: [
    {
      id: "usa-work-green",
      name: "АНУ ажлын виз, амьдрах эрх авах",
      description: "EB3, EB1-2, NIW, H2B, H1B, O1, L1 ангиллаар ажлын виз, ногоон карт авах зөвлөгөө",
      bullets: [
        "EB3 Unskilled — өндөр ур чадвар шаардахгүй, гэр бүлээрээ ажлын шугамаар ногоон карт авах",
        "EB NIW — ажил олгогчгүйгээр өөрөөрөө, гэр бүлээрээ ногоон карт авах",
        "EB1-2 — онцгой чадвар эсвэл нэрт профессорт зориулсан ногоон карт",
        "H2B — улирлын ажилчдад зориулсан ажлын виз",
        "H1B — мэргэжлийн ажилчдад зориулсан ажлын виз",
        "O1 — онцгой авьяас чадвартай хүмүүст зориулсан виз",
        "L1 — компанийн дотоод шилжилт хөдөлгөөний виз",
        "Таньд тохирсон ангиллыг тодорхойлох",
      ],
      duration: "60 минут",
      price: 99000,
      qpayLink: "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f",
      badge: "Ажлын виз & Ногоон карт",
      badgeColor: "from-green-600 to-emerald-500",
    },
    {
      id: "usa-travel",
      name: "АНУ аялал B1/B2",
      description: "Аялалын виз мэдүүлэх бүрэн зөвлөгөө",
      bullets: [
        "Анх удаа эсвэл дахин мэдүүлэх стратеги",
        "Бондоо найдвартай буцааж авах арга",
        "DS-160 анкет шинээр үүсгэх",
        "Таны эрсдлийг тодорхойлох",
        "Шаардлагатай материалын жагсаалт",
        "Ярилцлагад бэлдэх зөвлөмж",
        "Зардлын тооцоолол",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: "https://paylink.mn/api/s/inv_mrz8edg071e04d",
    },
    {
      id: "usa-study",
      name: "АНУ оюутны F-1 виз",
      description: "Оюутны виз мэдүүлэх бүрэн зөвлөгөө",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Статусаа хадгалах",
        "Хөтөлбөр, анги шилжэхэд анхаарах зүйлс",
        "I-20 болон SEVIS бүртгэл",
        "Ярилцлагад бэлдэх",
        "Ажил эрхлэх боломжууд (OPT/CPT)",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "usa-immigration",
      name: "АНУ цагаачлал",
      description: "Цагаачлал болон орогнолын асуудлаар зөвлөгөө",
      bullets: [
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Орогнолын асуудал",
        "Ажлын спонсор олоход туслах",
        "Таны онцлогт тохирсон бусад боломжууд",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("АНУ"),
  ],
  australia: [
    {
      id: "au-travel",
      name: "Австрали аяллын виз",
      description: "Visitor visa (subclass 600) зөвлөгөө",
      bullets: [
        "Online profile нээх",
        "Материалын тайлбар",
        "Захидал бичих",
        "Санхүүгийн баталгаа",
        "Аяллын төлөвлөгөө",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "au-study",
      name: "Австрали оюутны виз",
      description: "Student visa (subclass 500) зөвлөгөө",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд",
        "CoE бичиг баримт",
        "Эрүүл мэндийн шалгалт",
        "Тэтгэлэгт зуучлах",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "au-immigration",
      name: "Австрали цагаачлал",
      description: "Австрали улсад байнга оршин суух, ажлын визний бүрэн зөвлөгөө",
      bullets: [
        "Skilled Independent visa (subclass 189)",
        "Employer Sponsored visa (subclass 482/186)",
        "Regional Sponsored Migration Scheme (subclass 187)",
        "Цагаачлалын өмгөөлөгчтэй холбох",
        "Таны онцлогт тохирсон ажлын визний төрлийг тодорхойлох",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("Австрали"),
  ],
  canada: [
    {
      id: "ca-travel",
      name: "Канад аяллын виз",
      description: "Visitor visa / eTA зөвлөгөө",
      bullets: [
        "GCKEY profile нээх",
        "Материалын тайлбар",
        "Захидал бичих",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго тайлбарлах",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ca-study",
      name: "Канад оюутны виз",
      description: "Study permit зөвлөгөө",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Study permit мэдүүлэх",
        "Сургууль сонгох зөвлөгөө",
        "Acceptance letter авах",
        "Биометр бүртгэл",
        "Ажил эрхлэх боломжууд (Co-op/PGWP)",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    {
      id: "ca-immigration",
      name: "Канад цагаачлал",
      description: "Express Entry болон бусад цагаачлалын хөтөлбөрийн зөвлөгөө",
      bullets: [
        "Express Entry системийн тайлбар",
        "Таны оноог нэмэгдүүлэх арга",
        "Магадлал нэмэгдүүлэх стратеги",
        "Цагаачлалын өмгөөлөгчтэй холбох",
      ],
      duration: "45 минут",
      price: 159000,
      qpayLink: IMMIGRATION_LINK,
    },
    VIP_SERVICE("Канад"),
  ],
  schengen: [
    {
      id: "sch-travel",
      name: "Шенген аяллын виз",
      description: "Шенген бүс нутгийн аяллын виз",
      bullets: [
        "Элчин сайдын газар сонгох",
        "Материал бүрдүүлэх",
        "Цаг захиалах заавар",
        "Татгалзлыг бууруулах арга",
        "Санхүүгийн баталгаа",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
  ],
  czech: [
    {
      id: "cz-work-contract",
      name: "Чехийн хөдөлмөрийн гэрээ",
      description: "Чех улсад ажиллах хөдөлмөрийн гэрээгээр явах бүрэн зөвлөгөө",
      bullets: [
        "Гэрээт үйлдвэр, компаниудад зуучлах",
        "Элчин сайдын газарт цаг захиалах",
        "Материал бүрдүүлэх заавар",
        "Нисэх онгоцны буудлаас тосох",
        "Байр, орон сууцны асуудал",
        "Хөдөлмөрийн зөвшөөрлийн хүсэлт",
      ],
      duration: "60 минут",
      price: 99000,
      qpayLink: "https://paylink.mn/api/s/inv_ms0ubhod5f9c1f",
      badge: "Хөдөлмөрийн гэрээ",
      badgeColor: "from-orange-500 to-amber-500",
    },
  ],
  ireland: [
    {
      id: "ie-travel",
      name: "Ирланд аяллын виз",
      description: "Ирланд улсын аяллын виз",
      bullets: [
        "Материал бүрдүүлэх",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "ie-study",
      name: "Ирландийн сургалт, ажлын виз",
      description: "Ирланд улсад суралцах болон ажлын виз — ажлын виз, гэр бүлээрээ явах, хэлний курсээр явах боломж",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Материал бүрдүүлэлт",
        "Давж заалдалт",
        "Төлбөрөө хурдан буцааж авах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "Ажлын виз авах арга зам",
        "Гэр бүлээрээ явах боломж",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  korea: [
    {
      id: "kr-travel",
      name: "Солонгос аяллын виз",
      description: "Солонгос улсын аяллын виз",
      bullets: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Аяллын зорилго"],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "kr-study",
      name: "Солонгос оюутны виз",
      description: "Солонгос улсад суралцах виз",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж (TOPIK)",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
    VIP_SERVICE("Солонгос"),
  ],
  uk: [
    {
      id: "uk-travel",
      name: "Их Британи аяллын виз",
      description: "UK Standard Visitor Visa зөвлөгөө",
      bullets: [
        "Материал бүрдүүлэх",
        "Санхүүгийн баталгаа",
        "Аяллын зорилго",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "uk-study",
      name: "Их Британи оюутны виз",
      description: "UK Student Visa зөвлөгөө",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд (20 цаг/7 хоног)",
        "CAS бичиг баримт",
        "Тэтгэлэгт зуучлах",
        "Татгалзлыг бууруулах арга",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
  "new-zealand": [
    {
      id: "nz-skilled",
      name: "Шинэ Зеланд — Мэргэжлээрээ ажиллах",
      description: "Мэргэжлийн боломжийг тодорхойлж, эртнээс бэлдэж эхлэх",
      bullets: [
        "Таны боломжийг мэргэжлийн бага тодорхойлно",
        "Эртнээс таныг бэлдэж эхлэх",
        "Хуулийн фирмтэй хамтарч ажиллах",
        "Skilled Migrant Category болон Essential Skills визний шаардлага",
        "Мэргэжлийг хүлээн зөвшөөрүүлэх арга",
        "Таньд тохирсон визийг тодорхойлох",
      ],
      duration: "45 минут",
      price: 69000,
      qpayLink: "https://paylink.mn/api/s/inv_mrzynhjf4a66af",
      badge: "Мэргэжлийн ажил",
      badgeColor: "from-orange-500 to-amber-500",
    },
    {
      id: "nz-travel",
      name: "Шинэ Зеланд аяллын виз",
      description: "Visitor Visa зөвлөгөө",
      bullets: ["Материал бүрдүүлэх", "Санхүүгийн баталгаа", "Аяллын зорилго"],
      duration: "30 минут",
      price: 39000,
      qpayLink: TRAVEL_LINK,
    },
    {
      id: "nz-study",
      name: "Шинэ Зеланд оюутны виз",
      description: "Student Visa зөвлөгөө",
      bullets: [
        "Гэрээт сургуулиудад зуучлах",
        "Хэлний курсээр суралцах боломж",
        "Ажил эрхлэх боломжууд",
        "Тэтгэлэгт зуучлах",
      ],
      duration: "45 минут",
      price: 59000,
      qpayLink: STUDY_LINK,
    },
  ],
};

const COUNTRY_NAMES: Record<string, string> = {
  usa: "АНУ 🇺🇸",
  australia: "Австрали 🇦🇺",
  canada: "Канад 🇨🇦",
  schengen: "Шенген 🇪🇺",
  czech: "Чех 🇨🇿",
  ireland: "Ирланд 🇮🇪",
  korea: "Солонгос 🇰🇷",
  uk: "Их Британи 🇬🇧",
  "new-zealand": "Шинэ Зеланд 🇳🇿",
};

export default function ConsultationServicePage() {
  const params = useParams<{ country: string; serviceId: string }>();
  const [, navigate] = useLocation();
  const [showBank, setShowBank] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const country = params.country?.toLowerCase() ?? "";
  const serviceId = params.serviceId?.toLowerCase() ?? "";

  const services = ALL_SERVICES[country] ?? [];
  const service = services.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #1a0a2e 50%, #0d1a3a 100%)" }}>
        <p className="text-white/60 text-lg mb-4">Үйлчилгээ олдсонгүй</p>
        <button onClick={() => navigate("/consultation")} className="text-violet-400 hover:text-violet-300 flex items-center gap-2">
          <ArrowLeft size={16} /> Буцах
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #0f0a1e 0%, #1a0a2e 50%, #0d1a3a 100%)" }}>
      {/* Floating orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }} />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full opacity-8 blur-3xl" style={{ background: "radial-gradient(circle, #4f46e5, transparent)" }} />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/consultation")}
          className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="text-sm">{COUNTRY_NAMES[country] ?? country} зөвлөгөө</span>
        </button>

        {/* Service card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-6"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
        >
          {service.badge && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r ${service.badgeColor} text-white text-xs font-semibold mb-4`}>
              <Star size={11} />
              {service.badge}
            </div>
          )}

          <h1 className="text-xl font-bold text-white mb-2">
            {service.name}
          </h1>
          <p className="text-white/50 text-sm mb-6">{service.description}</p>

          {/* Duration & Price */}
          <div className="flex items-center justify-between mb-6 p-3 rounded-xl" style={{ background: "rgba(124,58,237,0.1)", border: "1px solid rgba(124,58,237,0.2)" }}>
            <div className="flex items-center gap-2 text-white/70">
              <Clock size={14} />
              <span className="text-sm">{service.duration}</span>
            </div>
            <div className="text-white font-bold text-lg">₮{service.price.toLocaleString()}</div>
          </div>

          {/* Bullets */}
          <div className="mb-6">
            <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
              Зөвлөгөөнд багтах зүйлс
            </h2>
            <ul className="space-y-2">
              {service.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle size={14} className="text-violet-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/75 text-sm">{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="space-y-2">
            {/* QPay primary button */}
            <a
              href={service.qpayLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white transition-all hover:opacity-90 active:scale-95"
              style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)" }}
            >
              <ExternalLink size={16} />
              QPay-р төлж захиалах — ₮{service.price.toLocaleString()}
            </a>

            {/* QPay transfer note */}
            <p className="text-center text-[11px]" style={{ color: "rgba(167,139,250,0.45)" }}>
              ⚠️ Гүйлгээний утга: <span style={{ color: "rgba(167,139,250,0.7)" }}>утас, и-мэйл хаягаа</span>
            </p>

            {/* Bank accordion toggle */}
            <button
              onClick={() => setShowBank((v) => !v)}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-xs transition-all"
              style={{ color: "rgba(167,139,250,0.55)" }}
            >
              <span>QPay ажиллахгүй бол? → Дансаар төлөх</span>
              <motion.span animate={{ rotate: showBank ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown size={13} />
              </motion.span>
            </button>

            {/* Bank details accordion */}
            <AnimatePresence>
              {showBank && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                    <p className="text-xs font-semibold" style={{ color: "rgba(167,139,250,0.7)" }}>🏦 Хаан банк дансны мэдээлэл</p>

                    {/* Account number */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(167,139,250,0.45)" }}>Дансны дугаар</p>
                        <p className="text-white font-bold text-base tracking-widest">5113085618</p>
                      </div>
                      <button onClick={() => copyToClipboard("5113085618", "account")} className="p-1.5 rounded-lg transition-all hover:bg-white/10" style={{ color: copiedField === "account" ? "#6EE7B7" : "rgba(167,139,250,0.6)" }}>
                        {copiedField === "account" ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>

                    {/* IBAN */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(167,139,250,0.45)" }}>IBAN</p>
                        <p className="text-white font-semibold">28000500</p>
                      </div>
                      <button onClick={() => copyToClipboard("28000500", "iban")} className="p-1.5 rounded-lg transition-all hover:bg-white/10" style={{ color: copiedField === "iban" ? "#6EE7B7" : "rgba(167,139,250,0.6)" }}>
                        {copiedField === "iban" ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>

                    {/* Account name */}
                    <div>
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(167,139,250,0.45)" }}>Дансны нэр</p>
                      <p className="text-white font-semibold">Б.Ууганбаяр</p>
                    </div>

                    {/* Transfer note */}
                    <div className="rounded-lg px-3 py-2" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)" }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "rgba(251,191,36,0.6)" }}>⚠️ Гүйлгээний утга</p>
                      <p className="text-xs" style={{ color: "rgba(251,191,36,0.9)" }}>Утасны дугаар болон и-мэйл хаягаа бичнэ үү</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Note */}
        <p className="text-center text-white/30 text-xs mt-4">
          Төлбөр хийсний дараа манай зөвлөх тантай холбогдоно
        </p>
      </div>
    </div>
  );
}
